// ── Merging what a document says into a form ──────────────────────────────────────────────────────
// One walk, two rules. The POA smart intake (src/components/Poa/index.tsx) and the Last Will smart
// intake (src/components/LastWill/index.tsx) share this file so there is only ever one copy of the
// merge:
//
//  • mergeFill   — a NON-EMPTY extracted value OVERWRITES the current one. The document is
//                  authoritative, so reading it again can CORRECT a wrong value (e.g. an age of
//                  411 → 41). This is what POA has always done.
//  • mergeBlanks — only an EMPTY answer is filled in. An answer a member of staff has typed is
//                  never overwritten by a file — the Last Will rule, because that form is what a
//                  client signs, so whoever typed it keeps the last word.
//
// Under BOTH rules an empty extracted value keeps whatever is already there, so a second document
// that lacks a field never wipes it. Bilingual {en,th} sides are handled independently (they are
// simply two keys of one object).
//
// Lists are the caller's business, not this file's: POA grows its client list and the will fills its
// blank rows and then appends, so nothing here has to guess how two lists line up.
type Mode = 'source' | 'blanks';

function walk(target: any, src: any, mode: Mode): any {
  if (src == null) return target;
  if (typeof src === 'string') {
    if (String(src).trim() === '') return target;                                 // an empty reading never wipes
    if (mode === 'blanks' && String(target ?? '').trim() !== '') return target;   // … and never overwrites
    return src;
  }
  if (typeof src === 'object') {
    const t = (target && typeof target === 'object') ? { ...target } : {};
    for (const k of Object.keys(src)) t[k] = walk(t[k], src[k], mode);
    return t;
  }
  return target ?? src;
}

/** Fill a form from a document, the document winning every answer it actually carries. */
export const mergeFill = (target: any, src: any): any => walk(target, src, 'source');

/** Fill ONLY the blanks of a form from a document; anything already answered stays exactly as it is. */
export const mergeBlanks = (target: any, src: any): any => walk(target, src, 'blanks');
